import { useEffect, useState, useCallback } from "react";
import { Plus, RefreshCw, Trash2, Pencil, Zap, Server as ServerIcon, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const empty = { label: "", hostname: "", whm_username: "root", api_token: "", notes: "" };

export default function ServersPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [rotate, setRotate] = useState(null);
  const [newToken, setNewToken] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/servers");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      label: row.label, hostname: row.hostname,
      whm_username: row.whm_username, api_token: "", notes: row.notes || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const body = { ...form };
        if (!body.api_token) delete body.api_token;
        await api.patch(`/servers/${editing.id}`, body);
        toast.success("Server updated");
      } else {
        await api.post("/servers", form);
        toast.success("Server added");
      }
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const test = async (row) => {
    setTesting((t) => ({ ...t, [row.id]: true }));
    try {
      const { data } = await api.post(`/servers/${row.id}/test`);
      (data.ok ? toast.success : toast.error)(data.message);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTesting((t) => ({ ...t, [row.id]: false })); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/servers/${confirmDel.id}`);
      toast.success("Server deleted");
      setConfirmDel(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doRotate = async () => {
    if (!rotate) return;
    try {
      await api.post(`/servers/${rotate.id}/rotate-token`, { api_token: newToken });
      toast.success("Token rotated");
      setRotate(null); setNewToken("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="WHM Servers"
        description="Stores encrypted WHM API tokens. Health-check pings the WHM `applist` endpoint. Add stub hostnames containing 'example.com' to demo without a real server."
        testId="servers-header"
        actions={
          <>
            <Button variant="outline" size="sm" onClick={load} data-testid="servers-refresh-btn">
              <RefreshCw className="mr-1.5 h-4 w-4" /> Refresh
            </Button>
            <Button size="sm" onClick={openNew} data-testid="servers-add-btn">
              <Plus className="mr-1.5 h-4 w-4" /> Add server
            </Button>
          </>
        }
      />

      {loading ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={ServerIcon}
          title="No WHM servers yet"
          description="Add your first WHM server to start routing Halo users. You can use a hostname containing 'example.com' to try the flow with the built-in stub before pointing at real WHM."
          action={<Button size="sm" onClick={openNew} data-testid="empty-add-server-btn"><Plus className="mr-1.5 h-4 w-4" /> Add server</Button>}
          testId="servers-empty"
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40">
              <tr className="text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                <th className="px-4 py-2.5">Label</th>
                <th className="px-4 py-2.5">Hostname</th>
                <th className="px-4 py-2.5">WHM user</th>
                <th className="px-4 py-2.5">API token</th>
                <th className="px-4 py-2.5">Health</th>
                <th className="px-4 py-2.5">Last checked</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-muted/40" data-testid={`server-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium">{r.label}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.hostname}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.whm_username}</td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {r.has_token ? r.api_token_preview : <span className="text-muted-foreground italic">not set</span>}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={r.health} testId={`server-status-${r.id}`} />
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {r.last_checked_at ? new Date(r.last_checked_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button variant="outline" size="sm" onClick={() => test(r)} disabled={testing[r.id]} data-testid={`test-connection-btn-${r.id}`}>
                        <Zap className={`mr-1.5 h-3.5 w-3.5 ${testing[r.id] ? "animate-pulse" : ""}`} />
                        Test
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setRotate(r); setNewToken(""); }} data-testid={`rotate-token-btn-${r.id}`}>
                        <KeyRound className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`edit-server-btn-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDel(r)} data-testid={`delete-server-btn-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="server-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit WHM server" : "Add WHM server"}</DialogTitle>
            <DialogDescription>Tokens are encrypted at rest with Fernet. Only the last 4 characters are ever displayed.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label htmlFor="label">Label</Label>
              <Input id="label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="EU-DC1" required data-testid="server-label-input" />
            </div>
            <div>
              <Label htmlFor="hostname">Hostname</Label>
              <Input id="hostname" value={form.hostname} onChange={(e) => setForm({ ...form, hostname: e.target.value })}
                placeholder="whm1.example.com" required data-testid="server-hostname-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="user">WHM username</Label>
                <Input id="user" value={form.whm_username} onChange={(e) => setForm({ ...form, whm_username: e.target.value })}
                  placeholder="root" data-testid="server-user-input" />
              </div>
              <div>
                <Label htmlFor="token">API token {editing ? <span className="text-xs text-muted-foreground">(leave blank to keep)</span> : null}</Label>
                <Input id="token" type="password" value={form.api_token}
                  onChange={(e) => setForm({ ...form, api_token: e.target.value })}
                  placeholder="XXXX…" data-testid="server-token-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Internal notes, cluster info, reseller boundaries…" data-testid="server-notes-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="server-cancel-btn">Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="server-save-btn">{saving ? "Saving…" : editing ? "Save" : "Add server"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rotate token dialog */}
      <Dialog open={!!rotate} onOpenChange={(o) => { if (!o) setRotate(null); }}>
        <DialogContent data-testid="rotate-dialog">
          <DialogHeader>
            <DialogTitle>Rotate API token</DialogTitle>
            <DialogDescription>The old token is discarded. Only the last 4 chars of the new token are stored for display.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>New API token</Label>
            <Input type="password" value={newToken} onChange={(e) => setNewToken(e.target.value)} data-testid="rotate-token-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRotate(null)} data-testid="rotate-cancel-btn">Cancel</Button>
            <Button onClick={doRotate} disabled={!newToken} data-testid="rotate-save-btn">Rotate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this WHM server?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the server config and its stored API token. Existing mappings pointing at
              this server will need to be reassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
